import {
  fetchBlob,
  productionPlannerControllerListPartnerProductionPlanner,
  workOrdersPartnerControllerListJobCards,
  workOrdersPartnerControllerReorderManufacturingSequence,
} from '@artic/api-client';
import type {
  JobCardRowDto,
  ProductionPlannerRowDto,
  ReorderManufacturingSequenceDto,
  ReorderManufacturingSequenceResponseDtoMovedItem,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { PROJECT_PLANNING_KEY } from '../project-planning/queries';

export const PRODUCTION_PLANNER_KEY = 'production-planner' as const;

/**
 * Partner-wide production planner rows — same enriched shape the project
 * planning grid consumes, augmented with the ENG-571 tracking fields
 * (completed %, job-card progress, drawings, last-seen zone/station, actual
 * start/complete timestamps).
 */
export function partnerProductionPlannerQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [PRODUCTION_PLANNER_KEY, 'partner', partnerId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await productionPlannerControllerListPartnerProductionPlanner(partnerId, {
        signal,
      });
      return res.data as ProductionPlannerRowDto[];
    },
    enabled: !!partnerId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Job cards for a single work order — fetched lazily by the master/detail
 * child renderer when the user expands a row. The endpoint URL keeps the
 * `work-orders` segment because the domain concept (a work order's job
 * cards) is unaffected by the route rename.
 */
export function workOrderJobCardsQueryOptions(workOrderId: string | undefined) {
  return queryOptions({
    queryKey: [PRODUCTION_PLANNER_KEY, 'job-cards', workOrderId] as const,
    queryFn: async ({ signal }) => {
      if (!workOrderId) throw new Error('workOrderId is required');
      const res = await workOrdersPartnerControllerListJobCards(workOrderId, { signal });
      return res.data as JobCardRowDto[];
    },
    enabled: !!workOrderId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Reorder the Manufacturing Sequence Number for the selected work orders within
 * their factory (Production Planner action). Invalidates the production planner
 * AND the project planning grids, since both surface the number. Returns the
 * moved work orders (with their new numbers) so the caller can build the toast.
 */
export function useReorderManufacturingSequence(partnerId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: ReorderManufacturingSequenceDto) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await workOrdersPartnerControllerReorderManufacturingSequence(partnerId, params);
      return res.data as { moved: ReorderManufacturingSequenceResponseDtoMovedItem[] };
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'detail'] });
      void queryClient.invalidateQueries({ queryKey: [PROJECT_PLANNING_KEY, 'list'] });
    },
  });
}

/**
 * Download scan labels (a PDF, one 200mm × 63mm label per work order) for the
 * selected work orders (Production Planner action). Returns the PDF Blob so
 * the caller can trigger the browser download. Read-only on the server — no
 * cache invalidation. Uses `fetchBlob` because the response is binary and
 * `customFetch` always JSON-parses the body.
 */
export function useDownloadScanLabels(partnerId: string | undefined) {
  return useMutation({
    mutationFn: async (work_order_ids: string[]) => {
      if (!partnerId) throw new Error('partnerId is required');
      return fetchBlob(`/partners/${partnerId}/work-order-labels`, {
        method: 'POST',
        body: JSON.stringify({ work_order_ids }),
      });
    },
  });
}
